import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Badge,
  Tooltip,
  Snackbar,
  Fade,
  Zoom,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Image,
  VideoLibrary,
  Preview,
  OpenInNew,
  Add,
  Edit,
  Save,
  Cancel,
  CheckCircle,
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff,
} from '@mui/icons-material';
import { uploadApi } from '../../services/api';
import { MediaPreview } from './MediaPreview';
import { MediaItem, UploadResponse } from '../../types/upload';

interface MediaManagerProps {
  onMediaSelect?: (media: MediaItem) => void;
  onMediaUpdate?: (media: MediaItem) => void;
  onMediaDelete?: (mediaId: string) => void;
  multiple?: boolean;
  selectedMedia?: MediaItem[];
  onSelectionChange?: (media: MediaItem[]) => void;
  showUpload?: boolean;
  showPreview?: boolean;
  showEdit?: boolean;
  showDelete?: boolean;
  filterType?: 'all' | 'image' | 'video';
  maxFiles?: number;
  // 임시 업로드 관리를 위한 새로운 props
  onTempUpload?: (media: MediaItem) => void;
  onCancelUpload?: (mediaId: string) => void;
}

export const MediaManager: React.FC<MediaManagerProps> = ({
  onMediaSelect,
  onMediaUpdate,
  onMediaDelete,
  multiple = false,
  selectedMedia = [],
  onSelectionChange,
  showUpload = true,
  showPreview = true,
  showEdit = true,
  showDelete = true,
  filterType = 'all',
  maxFiles = 10,
  onTempUpload,
  onCancelUpload,
}) => {
  const [mediaList, setMediaList] = useState<MediaItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingMedia, setEditingMedia] = useState<MediaItem | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    tags: '',
  });
  const [dragActive, setDragActive] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [playingVideos, setPlayingVideos] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // 임시 업로드된 파일들을 추적
  const [tempUploads, setTempUploads] = useState<MediaItem[]>([]);

  // 미디어 목록 로드
  const loadMediaList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await uploadApi.getMediaList({
        type: filterType,
        sortBy: 'date',
        sortOrder: 'desc',
      });
      setMediaList(result.mediaList || []);
    } catch (error) {
      console.error('미디어 목록 로드 실패:', error);
      setError('미디어 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  React.useEffect(() => {
    // 목록 로드 제거 - 업로드 후 바로 선택되므로 필요없음
    // loadMediaList();
  }, [loadMediaList, filterType]);

  // 파일 업로드 처리
  const handleFileUpload = async (file: File) => {
    setUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      const result = await uploadApi.uploadFile(file, (progress) => {
        setUploadProgress(progress);
      }) as UploadResponse;
      
      const newMedia: MediaItem = {
        id: Date.now().toString(),
        url: result.fileUrl,
        name: file.name,
        type: file.type.startsWith('image/') ? 'image' : 'video',
        size: file.size,
        uploadDate: result.uploadDate || new Date().toISOString().split('T')[0],
        description: '',
        tags: [],
      };

      // 임시 업로드로 추가
      setTempUploads(prev => [...prev, newMedia]);
      
      // 임시 업로드 콜백 호출
      if (onTempUpload) {
        onTempUpload(newMedia);
      }
      
      // 업로드 완료 후 바로 선택 처리
      if (onMediaSelect) {
        onMediaSelect(newMedia);
      }
      
      // 업로드 완료 후 모든 상태 초기화
      setShowUploadDialog(false);
      setUploadProgress(0);
      setError('');
      
      // 성공 메시지 표시
      setSuccessMessage(`${file.name} 업로드가 완료되었습니다!`);
      setShowSuccess(true);
      
      // 파일 입력 리셋
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('업로드 실패:', error);
      setError('파일 업로드에 실패했습니다.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // 임시 업로드 취소 처리
  const handleCancelTempUpload = async (mediaId: string) => {
    try {
      const tempMedia = tempUploads.find(m => m.id === mediaId);
      if (!tempMedia) return;
      
      // 파일 URL에서 타입과 파일명 추출
      const urlParts = tempMedia.url.split('/');
      const type = urlParts[urlParts.length - 2]; // image 또는 video
      const filename = urlParts[urlParts.length - 1];
      
      // 서버에서 파일 삭제
      await uploadApi.deleteFile(type, filename);
      
      // 임시 업로드 목록에서 제거
      setTempUploads(prev => prev.filter(m => m.id !== mediaId));
      
      // 취소 콜백 호출
      if (onCancelUpload) {
        onCancelUpload(mediaId);
      }
      
      // 성공 메시지 표시
      setSuccessMessage('업로드된 파일이 삭제되었습니다.');
      setShowSuccess(true);
    } catch (error) {
      console.error('임시 업로드 취소 실패:', error);
      setError('파일 삭제에 실패했습니다.');
    }
  };

  // 컴포넌트 언마운트 시 임시 업로드 정리
  React.useEffect(() => {
    return () => {
      // 컴포넌트가 언마운트될 때 임시 업로드된 파일들을 정리
      // 단, 저장 완료된 파일은 삭제하지 않음
      tempUploads.forEach(async (media) => {
        try {
          const urlParts = media.url.split('/');
          const type = urlParts[urlParts.length - 2];
          const filename = urlParts[urlParts.length - 1];
          await uploadApi.deleteFile(type, filename);
        } catch (error) {
          console.error('임시 파일 정리 실패:', error);
        }
      });
    };
  }, []); // tempUploads 의존성 제거

  // 파일 선택 처리
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    
    if (!isImage && !isVideo) {
      setError('이미지 또는 영상 파일만 업로드 가능합니다.');
      return;
    }

    // 이전 상태 초기화
    setError('');
    setUploadProgress(0);

    handleFileUpload(file);
  };

  // 드래그 앤 드롭 처리
  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      // 이전 상태 초기화
      setError('');
      setUploadProgress(0);
      
      const input = fileInputRef.current;
      if (input) {
        input.files = event.dataTransfer.files;
        handleFileSelect({ target: { files: event.dataTransfer.files } } as any);
      }
    }
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(false);
  }, []);

  // 미디어 선택 처리
  const handleMediaSelect = (media: MediaItem) => {
    if (multiple) {
      const isSelected = selectedMedia.some(m => m.id === media.id);
      if (isSelected) {
        const newSelection = selectedMedia.filter(m => m.id !== media.id);
        onSelectionChange?.(newSelection);
      } else {
        if (selectedMedia.length < maxFiles) {
          const newSelection = [...selectedMedia, media];
          onSelectionChange?.(newSelection);
        }
      }
    } else {
      onMediaSelect?.(media);
      // 단일 선택 시 성공 메시지 표시
      setSuccessMessage(`${media.name}이(가) 선택되었습니다!`);
      setShowSuccess(true);
    }
  };

  // 비디오 재생/일시정지 토글
  const toggleVideoPlay = (mediaId: string) => {
    setPlayingVideos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(mediaId)) {
        newSet.delete(mediaId);
      } else {
        newSet.add(mediaId);
      }
      return newSet;
    });
  };

  // 미디어 편집 처리
  const handleEditMedia = (media: MediaItem) => {
    setEditingMedia(media);
    setEditForm({
      name: media.name,
      description: media.description || '',
      tags: media.tags?.join(', ') || '',
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editingMedia) return;

    try {
      const filename = editingMedia.url.split('/').pop() || '';
      const type = editingMedia.type;
      
      await uploadApi.updateMediaInfo(type, filename, {
        name: editForm.name,
        description: editForm.description,
        tags: editForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
      });

      const updatedMedia: MediaItem = {
        ...editingMedia,
        name: editForm.name,
        description: editForm.description,
        tags: editForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
      };

      setMediaList(prev => 
        prev.map(media => 
          media.id === editingMedia.id ? updatedMedia : media
        )
      );

      onMediaUpdate?.(updatedMedia);
      setShowEditDialog(false);
      setEditingMedia(null);
      
      // 성공 메시지 표시
      setSuccessMessage('미디어 정보가 업데이트되었습니다!');
      setShowSuccess(true);
    } catch (error) {
      console.error('미디어 정보 업데이트 실패:', error);
      setError('미디어 정보 업데이트에 실패했습니다.');
    }
  };

  // 미디어 삭제 처리
  const handleDeleteMedia = async (mediaId: string) => {
    try {
      const media = mediaList.find(m => m.id === mediaId);
      if (!media) return;
      
      // 파일 URL에서 타입과 파일명 추출
      const urlParts = media.url.split('/');
      const type = urlParts[urlParts.length - 2]; // images 또는 videos
      const filename = urlParts[urlParts.length - 1];
      
      await uploadApi.deleteFile(type, filename);
      setMediaList(prev => prev.filter(media => media.id !== mediaId));
      onMediaDelete?.(mediaId);
      
      // 성공 메시지 표시
      setSuccessMessage(`${media.name}이(가) 삭제되었습니다!`);
      setShowSuccess(true);
    } catch (error) {
      console.error('미디어 삭제 실패:', error);
      setError('미디어 삭제에 실패했습니다.');
    }
  };

  // 필터링된 미디어 목록
  const filteredMedia = mediaList.filter(media => {
    if (filterType === 'all') return true;
    return media.type === filterType;
  });

  // 선택된 미디어 확인
  const isSelected = (media: MediaItem) => {
    return selectedMedia.some(m => m.id === media.id);
  };

  // 파일 크기 포맷팅
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box>
      {/* 단순 업로드 영역만 표시 */}
      {showUpload && (
        <Paper
          sx={{
            border: `2px dashed ${dragActive ? 'primary.main' : '#ccc'}`,
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: dragActive ? 'action.hover' : 'transparent',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover',
            },
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => setShowUploadDialog(true)}
        >
          {uploading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                업로드 중...
              </Typography>
              <Box sx={{ width: '100%', mb: 2 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" color="textSecondary">
                  {uploadProgress}% 완료
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box>
              <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                파일 업로드
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {dragActive ? '파일을 여기에 놓으세요' : '이미지 또는 영상을 드래그하거나 클릭하여 업로드하세요'}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* 업로드 다이얼로그 */}
      <Dialog
        open={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>파일 업로드</DialogTitle>
        <DialogContent>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          
          <Box sx={{ textAlign: 'center', py: 3 }}>
            {uploading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  업로드 중...
                </Typography>
                <Box sx={{ width: '100%', mb: 2 }}>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                  <Typography variant="caption" color="textSecondary">
                    {uploadProgress}% 완료
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box>
                <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  파일 선택
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  이미지 또는 영상 파일을 선택하세요
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => fileInputRef.current?.click()}
                >
                  파일 선택
                </Button>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUploadDialog(false)}>취소</Button>
        </DialogActions>
      </Dialog>

      {/* 에러 알림 */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* 성공 알림 */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          severity="success" 
          onClose={() => setShowSuccess(false)}
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}; 